import { createContext, useCallback, useEffect, useState } from 'react';

import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from '@crehana/i18n';
import { useMe } from '@crehana/web';
import clone from 'lodash/clone';
import isEmpty from 'lodash/isEmpty';
import merge from 'lodash/merge';
import omit from 'lodash/omit';
import dynamic from 'next/dynamic';

import localeEs from '@/views/AdminDashboard/i18n/localeEs';

import { MyCustomHelper, StepContent } from './components';
import GET_USER_TOUR_QUERY from './graphql/GetUserTourQuery.v4.b2b.graphql';
import type {
  GetUserTourQuery,
  GetUserTourQueryVariables,
} from './graphql/types/GetUserTourQuery';
import type {
  UpdateUserTourMutation,
  UpdateUserTourMutationVariables,
} from './graphql/types/UpdateUserTourMutation';
import UPDATE_USER_TOUR_MUTATION from './graphql/UpdateUserTourMutation.v4.b2b.graphql';
import type {
  OptionsProps,
  SimpleReactourProps,
  StepProps,
  TourContextState,
  UpdateVariables,
} from './types';

const Tour = dynamic(() => import('reactour'), { ssr: false });

export const TourContext = createContext<TourContextState | null>(null);

export const TourProvider: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => {
  const { t } = useTranslation();
  const [showSecondTour, setShowSecondTour] = useState(false);
  const [props, setProps] = useState<SimpleReactourProps>({
    isOpen: false,
    steps: [],
    // biome-ignore lint/suspicious/noEmptyBlockStatements: intentional empty block
    onRequestClose: () => {},
  });
  const [options, setOptions] = useState<OptionsProps | null>(null);
  const [steps, setSteps] = useState<StepProps[]>([]);
  const commonProps = {
    className: 'max-w-360',
    scrollDuration: 200,
    maskClassName: 'react-tour-mask',
    maskSpace: 19,
    disableInteraction: true,
    closeWithMask: false,
  };

  const { me } = useMe();

  const variables = {
    userId: me?.originalId || 0,
  };
  const context = { clientName: 'v4.b2b' };

  const { data } = useQuery<GetUserTourQuery, GetUserTourQueryVariables>(
    GET_USER_TOUR_QUERY,
    {
      variables,
      context,
    },
  );
  const [updateTourMutation] = useMutation<
    UpdateUserTourMutation,
    UpdateUserTourMutationVariables
  >(UPDATE_USER_TOUR_MUTATION, {
    context,
    refetchQueries: [
      {
        query: GET_USER_TOUR_QUERY,
        variables,
        context,
      },
    ],
  });

  const open = useCallback(() => {
    if (data && options) {
      setProps(p =>
        merge(clone(p), {
          isOpen: options?.open ? options.open(data) : false,
        }),
      );
    }
  }, [data, options]);

  const updateTour = useCallback(
    (updateTourOptions: UpdateVariables) =>
      updateTourMutation(
        merge(
          {
            variables: {
              input: merge(
                omit(data?.userTour, ['__typename', 'organizationUserId']),
                { organizationUserId: variables.userId },
              ),
            },
          },
          updateTourOptions,
        ),
      ),
    [data?.userTour, updateTourMutation, variables.userId],
  );

  const handleCloseFirstTour = () => {
    setProps(p => merge(clone(p), { isOpen: false }));
    if (!options?.onBeforeClose) {
      setShowSecondTour(true);
    }
  };

  const handleForceCloseFirstTour = () => {
    // biome-ignore lint/suspicious/noEmptyBlockStatements: intentional empty block
    setProps(p => merge(clone(p), { isOpen: false, onBeforeClose: () => {} }));
    setShowSecondTour(true);
  };

  const handleCloseSecondTour = useCallback(() => {
    if (options?.close) {
      updateTour({
        variables: {
          input: options?.close,
        },
      });
    }
    setShowSecondTour(false);
  }, [options?.close, updateTour]);

  const handleGoToStep = () => {
    setProps(p => merge(clone(p), { isOpen: true }));
    setShowSecondTour(false);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  useEffect(() => {
    if (props.isOpen) {
      setProps(p =>
        merge(clone(p), { goToStep: 0, onBeforeClose: options?.onBeforeClose }),
      );
    }
  }, [props.isOpen]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  useEffect(() => {
    const value: SimpleReactourProps = clone(props);

    if (data && options?.defaultOpen === true && options?.open) {
      value.isOpen = options?.open(data);
    }

    if (!isEmpty(steps)) {
      value.steps =
        steps?.map(step => ({
          ...step,
          content: <StepContent {...step.content} />,
        })) || [];
    }

    if (options?.onBeforeClose) {
      value.onBeforeClose = options.onBeforeClose;
    }

    if (!isEmpty(value?.steps)) {
      setProps(value);
    }
  }, [steps, data, options]);

  return (
    <TourContext.Provider
      value={{
        isOpen: props.isOpen || showSecondTour,
        steps,
        setSteps,
        open,
        props,
        setProps,
        options,
        setOptions,
        updateTour,
      }}
    >
      {children}
      {!isEmpty(props?.steps) && (
        <>
          <Tour
            {...props}
            {...commonProps}
            disableKeyboardNavigation={['esc']}
            onRequestClose={handleCloseFirstTour}
            CustomHelper={helperProps => (
              <MyCustomHelper
                onClose={handleForceCloseFirstTour}
                {...helperProps}
              />
            )}
          />
          <Tour
            {...commonProps}
            isOpen={showSecondTour}
            disableKeyboardNavigation={['esc']}
            onRequestClose={handleCloseSecondTour}
            CustomHelper={helperProps => (
              <MyCustomHelper
                {...helperProps}
                isSecondTour
                gotoStep={handleGoToStep}
              />
            )}
            steps={[
              {
                selector: '[data-tour="admin-guide"]',
                position: 'center',
                content: (
                  <StepContent
                    title={t('TOUR_TITLE_LAST', {
                      defaultValue: localeEs.es.TOUR_TITLE_LAST,
                    })}
                    image="https://crehana-vercel-images-prd.imgix.net/public/aa6c6eea-a119-47fb-b8cb-871d9b419cf0-tour-image.jpg?auto=format&fit=clip"
                    description={t('TOUR_DESCRIPTION_LAST', {
                      defaultValue: localeEs.es.TOUR_DESCRIPTION_LAST,
                    })}
                  />
                ),
                style: {
                  maxWidth: 500,
                },
              },
            ]}
          />
        </>
      )}
    </TourContext.Provider>
  );
};
